/* Quote form — the customer's Get Quote: lane, cargo, pickup, extra services.
 *
 * Rebuilt 2026-11-03 on the shared quoting toolkit (src/newquote/*) so the
 * portal and the staff New Quote are ONE implementation of the cargo table,
 * the DG picker, the typeahead and the pickup flow — the 2026-08 port had
 * its own copies and drifted (no EACH/TOTAL, bilingual headers, a pickup
 * block that was never sent, no validation: an empty form priced 100 kg).
 *
 * Customer-only differences: lanes come from /public/quote-lanes (only
 * quotable lanes), commodities from /public/commodities (never Special /
 * Custom), pickup calls the /portal/* twins, extra services are a request
 * flag staff quote by hand, and nothing staff-only exists here. */
import { useState, useEffect, useMemo, C, Ic, F, Steps } from './common.js';
import { PiecesEditor, computeTotals, newPiece } from '../newquote/pieces.js';
import { DgPicker, matchDgItems } from '../newquote/dg_picker.js';
import { TypeAhead, UnitToggle } from '../newquote/inputs.js';
import { usePickup, PickupPanel } from '../newquote/pickup.js';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// quote-lanes rows → TypeAhead options: code = the lane code the quote POST
// expects, locode = IATA, name = the label without its "(IATA)" tail.
const laneOptions = (rows) => (rows || []).map(o => ({
  code: o.code, locode: o.iata, name: (o.label || '').replace(/\s*\([A-Z]{3}\)\s*$/, '') || o.code,
}));
const laneRender = (o) => <>
  <span className="mono bc" style={{ fontSize: 13, fontWeight: 700, color: C.blue, marginRight: 8 }}>{o.locode}</span>
  <span style={{ color: C.text }}>{o.name}</span>
</>;
const laneSelected = (o) => `${o.locode} — ${o.name}`;
const hl = { borderColor: '#d97706', boxShadow: '0 0 0 3px rgba(217,119,6,.18)' };

const QuoteForm = ({ setScreen, setQR, fd }) => {
  const [lanes, setLanes] = useState({ origins: [], dests: [] });
  const [commodities, setCommodities] = useState([]);
  const [dgItems, setDgItems] = useState([]);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    fetch('/api/elcapp/public/quote-lanes').then(r => r.json())
      .then(d => setLanes({ origins: d.origins || [], dests: d.dests || [] })).catch(() => {});
    fetch('/api/elcapp/public/commodities').then(r => r.json())
      .then(d => setCommodities(d.commodities || [])).catch(() => {});
    fetch('/api/elcapp/dg-items').then(r => r.json())
      .then(d => setDgItems(d.items || [])).catch(() => {});
    // Last 5 searches; no_rate / markup_gap rows come back greyed so the
    // customer can retry the lane on another day.
    fetch('/api/elcapp/quote/recent-searches?limit=5')
      .then(r => r.ok ? r.json() : { searches: [] })
      .then(d => setRecent(d.searches || [])).catch(() => {});
  }, []);
  const originOpts = useMemo(() => laneOptions(lanes.origins), [lanes]);
  const destOpts   = useMemo(() => laneOptions(lanes.dests),   [lanes]);

  const [f, sf] = useState({ org: '', dst: '', date: todayIso(), type: 'General Cargo' });
  const s = (k, v) => sf(p => ({ ...p, [k]: v }));
  // Landing-hero handoff (`fd`): match its "<name> (IATA)" strings to lanes.
  useEffect(() => {
    if (!lanes.origins.length) return;
    const iata = (x) => { const m = (x || '').match(/\(([A-Z]{3})\)/); return m ? m[1] : ''; };
    const heroOrg = (lanes.origins.find(o => o.iata === iata(fd && fd.origin)) || {}).code;
    const heroDst = (lanes.dests.find(o => o.iata === iata(fd && fd.dest)) || {}).code;
    sf(p => ({ ...p,
      org: heroOrg || (lanes.origins.some(o => o.code === p.org) ? p.org : ''),
      dst: heroDst || (lanes.dests.some(o => o.code === p.dst) ? p.dst : ''),
      date: (fd && fd.date) || p.date }));
  }, [lanes, fd]);

  const [pieces, setPieces]   = useState([newPiece()]);
  const [wtUnit, setWtUnit]   = useState('KG');
  const [dimUnit, setDimUnit] = useState('INCH');
  const [wtMode, setWtMode]   = useState('EACH');
  const [dgPicked, setDgPicked] = useState([]);
  const totals = useMemo(() => computeTotals(pieces, wtUnit, dimUnit, wtMode), [pieces, wtUnit, dimUnit, wtMode]);
  const isDg = f.type === 'Dangerous Goods';

  // Extra services: a request, not a price (Luke 2026-11-03) — staff quote them.
  const [svcReq, setSvcReq]   = useState(false);
  const [svcNote, setSvcNote] = useState('');

  const pickup = usePickup({ api: '/api/elcapp/portal/' });
  // Closest station vs an Origin the customer already picked: ask, never overwrite.
  const [originConflict, setOriginConflict] = useState(null);
  const laneForStation = (c) => lanes.origins.find(o => o.iata === (c.airport_code || '').toUpperCase());
  const onPickupFound = (cands) => {
    setOriginConflict(null);
    if (!cands.length) return;
    const lane = laneForStation(cands[0]);
    if (!lane) return;
    if (!f.org || f.org === lane.code) s('org', lane.code);
    else setOriginConflict({ code: lane.code, airport: cands[0].airport_code, prevOrigin: f.org });
  };
  const onPickupSelect = (c) => { const lane = laneForStation(c); if (lane) s('org', lane.code); setOriginConflict(null); };

  const [tried, setTried] = useState(false);
  const blockers = [];
  if (!f.org) blockers.push({ key: 'org', msg: 'Pick an origin airport' });
  if (!f.dst) blockers.push({ key: 'dst', msg: 'Pick a destination airport' });
  if (!(totals.chargeable > 0)) blockers.push({ key: 'weight', msg: 'Enter the weight (or dimensions) of at least one piece' });
  if (pickup.enabled && !pickup.selected) blockers.push({ key: 'pickup', msg: 'Find the nearest station for your pickup address, or turn pickup off' });
  const blocked = new Set(tried ? blockers.map(b => b.key) : []);

  const submit = () => {
    setTried(true);
    if (blockers.length) return;
    const sc = pickup.selectedCandidate;
    setQR({
      ...f, chargeWt: totals.chargeable, pieces,
      cargo_details: {
        pieces, wt_unit: wtUnit, dim_unit: dimUnit, wt_mode: wtMode,
        actual_kg: totals.actual, vol_kg: totals.volume,
        dg_un_items: isDg ? dgPicked.map(x => ({ un_id: x.un_id, name: x.name })) : [],
        dg_un_count: isDg ? dgPicked.length : null,
      },
      pickup: pickup.payload(),
      pickupLabel: pickup.payload() ? [pickup.shipper.city, pickup.shipper.state].filter(Boolean).join(', ') || pickup.address.trim() : '',
      pickupStation: sc ? sc.airport_code : '',
      services_requested: svcReq,
      services_note: svcReq ? svcNote.trim() : '',
    });
    setScreen('results');
  };

  const relTime = (iso) => {
    const t = iso ? new Date(iso).getTime() : 0;
    if (!t) return '';
    const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (sec < 60)    return 'just now';
    if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  };
  const applyRecent = (r) => {
    sf(p => ({ ...p,
      org:  lanes.origins.some(o => o.code === r.origin) ? r.origin : p.org,
      dst:  lanes.dests.some(o => o.code === r.dest)     ? r.dest   : p.dst,
      type: r.commodity || p.type,
      date: (r.ready_day || '').slice(0, 10) || p.date }));
    const cd = r.cargo_details || {};
    if (Array.isArray(cd.pieces) && cd.pieces.length) setPieces(cd.pieces.map((p, i) => ({ ...p, id: Date.now() + i })));
    if (cd.wt_unit)  setWtUnit(cd.wt_unit);
    if (cd.dim_unit) setDimUnit(cd.dim_unit);
    setWtMode(cd.wt_mode === 'TOTAL' ? 'TOTAL' : 'EACH');
    setDgPicked(matchDgItems(cd.dg_un_items, dgItems));
    setSvcReq(!!cd.services_requested);
    setSvcNote(cd.services_note || '');
  };

  return <div style={{ maxWidth: 860, margin: '0 auto', padding: '36px 20px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: C.textD, fontSize: 12, marginBottom: 26 }}>
      <span onClick={() => setScreen('landing')} style={{ cursor: 'pointer', color: C.textM }}>Home</span><Ic.ChevR />
      <span style={{ color: C.blueL }}>Get Quote</span>
    </div>
    <Steps cur={0} />
    <h2 className="bc" style={{ fontSize: 30, fontWeight: 700, marginBottom: 26 }}>AIR FREIGHT QUOTE</h2>
    {recent.length > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: C.textD, letterSpacing: '.1em', textTransform: 'uppercase', marginRight: 2 }}>Recent</span>
      {recent.map((r) => {
        const dim = r.pricing_source === 'no_rate' || r.pricing_source === 'markup_gap';
        return <button key={r.search_ref} onClick={() => applyRecent(r)}
          title={`${r.search_ref}${r.price != null ? ` · $${Number(r.price).toFixed(2)}` : ''}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 14, border: `1px solid ${C.brd}`,
                   background: dim ? 'rgba(0,0,0,0.03)' : 'rgba(37,99,235,0.06)', color: dim ? C.textD : C.blueL,
                   fontSize: 11, cursor: 'pointer', fontFamily: 'Barlow,sans-serif' }}>
          <span className="mono" style={{ fontWeight: 700 }}>{r.origin} → {r.dest}</span>
          <span style={{ color: C.textD }}>·</span><span>{r.commodity || '—'}</span>
          <span style={{ color: C.textD }}>·</span><span>{Number(r.charge_weight || 0).toFixed(0)}kg</span>
          <span style={{ color: C.textD }}>·</span><span style={{ color: C.textD }}>{relTime(r.created_at)}</span>
        </button>;
      })}
    </div>}

    <div style={{ display: 'grid', gap: 18 }}>
      <div className="card" style={{ padding: 24 }}>
        <div className="sec-hdr">Route Information</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <TypeAhead label="Origin airport" value={f.org} onChange={v => s('org', v)} options={originOpts}
                     placeholder="LAX, Los Angeles…" render={laneRender} formatSelected={laneSelected} highlight={blocked.has('org')} />
          <TypeAhead label="Destination airport" value={f.dst} onChange={v => s('dst', v)} options={destOpts}
                     placeholder="PVG, Shanghai…" render={laneRender} formatSelected={laneSelected} highlight={blocked.has('dst')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 240px', gap: 12 }}>
          <F label="Ready Date"><input type="date" value={f.date} onChange={(e) => s('date', e.target.value)} /></F>
          <F label="Cargo Type">
            <select value={f.type} onChange={(e) => s('type', e.target.value)}>
              {(commodities.length ? commodities : [f.type]).map((t) => <option key={t}>{t}</option>)}
            </select>
          </F>
        </div>
      </div>

      <div className="card" style={{ padding: 24, ...(blocked.has('weight') ? hl : {}) }}>
        <div className="sec-hdr">Cargo Information</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, color: C.textD, marginRight: 4 }}>Units:</div>
          <UnitToggle value={wtUnit} onChange={setWtUnit} options={['KG', 'LBS']} />
          <div style={{ fontSize: 11, color: C.textD, marginLeft: 8, marginRight: 4 }}>Dimensions:</div>
          <UnitToggle value={dimUnit} onChange={setDimUnit} options={['CM', 'INCH']} />
          <div style={{ fontSize: 11, color: C.textD, marginLeft: 8, marginRight: 4 }}>Weight is:</div>
          <UnitToggle value={wtMode} onChange={setWtMode} options={['EACH', 'TOTAL']} />
          <span style={{ fontSize: 10, color: C.textD }}>
            {wtMode === 'TOTAL' ? 'row weight = total for the line (not × qty)' : 'row weight = per piece (× qty)'}
          </span>
        </div>
        {isDg && <DgPicker items={dgItems} picked={dgPicked} setPicked={setDgPicked} />}
        <PiecesEditor pieces={pieces} setPieces={setPieces} wtUnit={wtUnit} dimUnit={dimUnit} wtMode={wtMode} />
      </div>

      <PickupPanel p={pickup} onFound={onPickupFound} onSelect={onPickupSelect} highlight={blocked.has('pickup')}>
        {originConflict && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.35)', borderRadius: 8,
                        fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>Closest station is <b className="mono">{originConflict.airport}</b> but Origin is <b className="mono">{originConflict.prevOrigin}</b>.</span>
            <span style={{ display: 'inline-flex', gap: 8 }}>
              <button className="btn bb" style={{ fontSize: 11, padding: '5px 10px' }} onClick={() => { s('org', originConflict.code); setOriginConflict(null); }}>Use {originConflict.airport}</button>
              <button className="btn bo" style={{ fontSize: 11, padding: '5px 10px' }} onClick={() => setOriginConflict(null)}>Keep {originConflict.prevOrigin}</button>
            </span>
          </div>
        )}
      </PickupPanel>

      <div className="card" style={{ padding: 22 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', fontSize: 13, color: C.text }}>
          <input type="checkbox" checked={svcReq} onChange={e => setSvcReq(e.target.checked)} style={{ width: 'auto' }} />
          <span>I need extra services <span style={{ color: C.textD, fontSize: 11 }}>(repacking, palletizing, labeling… — quoted separately by our team)</span></span>
        </label>
        {svcReq && <div style={{ marginTop: 12 }}>
          <F label="What do you need? (optional)">
            <input type="text" maxLength={500} value={svcNote} onChange={e => setSvcNote(e.target.value)} placeholder="e.g. shrink-wrap and palletize 12 cartons" />
          </F>
        </div>}
      </div>

      {tried && blockers.length > 0 && (
        <div style={{ padding: '10px 14px', background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.35)', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
          <b>Before we can quote:</b> {blockers.map(b => b.msg).join(' · ')}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn bg" style={{ fontSize: 15, padding: '13px 40px' }} onClick={submit}>GET QUOTE →</button>
      </div>
    </div>
  </div>;
};

export { QuoteForm };
